<template>
  <div class="login-shell">
    <div class="ambient" />
    <div class="card">
      <div class="logo">
        <EcosaWordmark :size="28" />
      </div>
      <h1 class="title">AI Product Manager</h1>
      <p class="sub">Sign in to discuss requirements with your AI PM.</p>

      <form @submit.prevent="onSubmit" class="form">
        <label class="label">Email</label>
        <input
          v-model="form.email"
          class="input"
          type="email"
          autocomplete="email"
          autofocus
        />

        <label class="label" style="margin-top: 16px">Password</label>
        <input
          v-model="form.password"
          class="input"
          type="password"
          autocomplete="current-password"
        />

        <button type="submit" class="btn" :disabled="loading">
          {{ loading ? "Signing in…" : "Sign in" }}
          <span class="kbd">↵</span>
        </button>

        <p v-if="error" class="err">{{ error }}</p>
      </form>

      <div class="foot">By signing in you agree to Ecosa's internal tool policy.</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { login, fetchMe } from "@/api/auth";
import EcosaWordmark from "@/components/design/EcosaWordmark.vue";

const form = reactive({ email: "", password: "" });
const loading = ref(false);
const error = ref<string | null>(null);

const auth = useAuthStore();
const router = useRouter();
const route = useRoute();

async function onSubmit() {
  if (!form.email || !form.password) {
    error.value = "Please enter email and password.";
    return;
  }
  error.value = null;
  loading.value = true;
  try {
    const { access_token } = await login(form);
    auth.setToken(access_token);
    auth.setMe(await fetchMe());
    const redirect = (route.query.redirect as string) || "/";
    router.push(redirect);
  } catch (e: any) {
    error.value = e?.response?.data?.detail || "Login failed.";
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.login-shell {
  height: 100vh;
  width: 100vw;
  display: grid;
  place-items: center;
  background: var(--paper);
  position: relative;
  overflow: hidden;
}
.ambient {
  position: absolute;
  top: -20%;
  left: 50%;
  transform: translateX(-50%);
  width: 900px;
  height: 900px;
  background: radial-gradient(circle, #e8ddc9 0%, transparent 60%);
  opacity: 0.6;
  pointer-events: none;
}
.card {
  position: relative;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 36px 40px 28px;
  width: 380px;
  box-shadow: 0 20px 60px rgba(11, 15, 20, 0.06), 0 2px 8px rgba(11, 15, 20, 0.04);
}
.logo {
  margin-bottom: 28px;
}
.title {
  font-size: 22px;
  font-weight: 700;
  color: var(--ink);
  margin: 0 0 4px;
  letter-spacing: -0.02em;
}
.sub {
  margin: 0;
  color: var(--ink-3);
  font-size: 13px;
  line-height: 1.6;
}
.form {
  margin-top: 28px;
}
.label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: var(--ink-2);
  margin-bottom: 6px;
}
.input {
  width: 100%;
  background: var(--paper-2);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13.5px;
  color: var(--ink);
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.input:focus {
  border-color: var(--ink-3);
  background: var(--surface);
  box-shadow: 0 0 0 4px rgba(11, 15, 20, 0.04);
}
.btn {
  width: 100%;
  margin-top: 24px;
  background: var(--ink);
  color: #fff;
  border: none;
  padding: 11px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.btn:hover:not(:disabled) {
  background: #1a1f26;
}
.btn:active:not(:disabled) {
  transform: scale(0.99);
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.kbd {
  opacity: 0.5;
  font-size: 13px;
}
.err {
  color: #b03333;
  margin-top: 12px;
  font-size: 12.5px;
}
.foot {
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid var(--border);
  font-size: 11.5px;
  color: var(--ink-4);
  text-align: center;
  line-height: 1.55;
}
</style>
